import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import {
  Loader2,
  AlertCircle,
  MoreVertical,
} from 'lucide-react';
import ProviderLayout from '@/components/layout/ProviderLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import apiService from '@/services/api';

interface Job {
  id: number;
  booking_id?: number;
  quote_id?: number;
  customer_id?: number;
  customer_name: string;
  customer_email?: string;
  customer_phone?: string;
  service: string;
  service_id?: number;
  service_name?: string;
  service_category?: string;
  category_name?: string;
  address?: string;
  postcode?: string;
  booking_date?: string;
  created_at: string;
  total_amount?: string;
  total_amount_pence?: number;
  amount_pence?: number;
  amount_gbp?: string;
  status: 'completed';
  completed_at?: string;
  job_completed_at?: string;
  provider_accepted_at?: string;
  job_started_at?: string;
  updated_at?: string;
}

const ProviderCompletedJobsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState<'pdf' | 'csv' | 'excel'>('csv');
  const [exporting, setExporting] = useState(false);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const hasCheckedAuth = useRef(false);

  useEffect(() => {
    if (!authLoading && hasCheckedAuth.current === false) {
      hasCheckedAuth.current = true;
      if (!user || user.role !== 'provider') {
        navigate('/provider/login');
      }
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user?.id && !authLoading) {
      fetchCompletedJobs();
    }
  }, [user?.id, authLoading]);

  const fetchCompletedJobs = async () => {
    setLoading(true);
    try {
      const response = await apiService.get(`/provider/jobs?status=completed`);
      console.log('📊 API Response keys:', Object.keys(response?.data || {}));
      
      if (response.success && response.data) {
        let allJobs: any[] = [];
        
        // The API returns completed jobs in response.data.completed (grouped by status)
        // OR in response.data.jobs (filtered by query param)
        if (Array.isArray(response.data.completed) && response.data.completed.length > 0) {
          allJobs = response.data.completed;
          console.log(`✅ Got ${allJobs.length} jobs from response.data.completed`);
        } else if (Array.isArray(response.data.jobs) && response.data.jobs.length > 0) {
          allJobs = response.data.jobs;
          console.log(`✅ Got ${allJobs.length} jobs from response.data.jobs`);
        } else if (Array.isArray(response.data)) {
          allJobs = response.data;
          console.log(`✅ Got ${allJobs.length} jobs from response.data (array)`);
        }
        
        // Log first job to debug field names
        if (allJobs.length > 0) {
          console.log('📋 First job fields:', JSON.stringify(allJobs[0], null, 2));
        }
        
        // Transform API response to match Job interface
        const transformedJobs = allJobs.map((apiJob: any) => ({
          id: apiJob.id,
          quote_id: apiJob.quote_id,
          booking_id: apiJob.booking_id,
          customer_id: apiJob.customer_id,
          customer_name: `${apiJob.customer_name || ''} ${apiJob.last_name || ''}`.trim() || (apiJob.first_name ? `${apiJob.first_name} ${apiJob.last_name || ''}`.trim() : 'Unknown'),
          customer_email: apiJob.email || apiJob.customer_email,
          customer_phone: apiJob.phone || apiJob.customer_phone,
          service: apiJob.service || apiJob.service_name,
          service_id: apiJob.service_id,
          service_name: apiJob.service_name,
          service_category: apiJob.category_name || apiJob.service_category,
          category_name: apiJob.category_name,
          address: apiJob.address,
          postcode: apiJob.postcode,
          booking_date: apiJob.booking_date,
          // Use total_amount_pence if available and convert to GBP, otherwise use amount_gbp
          amount_gbp: apiJob.total_amount_pence 
            ? (apiJob.total_amount_pence / 100).toFixed(2)
            : (apiJob.amount_gbp || '0.00'),
          amount_pence: apiJob.total_amount_pence || apiJob.amount_pence,
          total_amount_pence: apiJob.total_amount_pence,
          status: apiJob.status,
          // Use the first available date field
          created_at: apiJob.created_at || apiJob.booking_date || new Date().toISOString(),
          completed_at: apiJob.completed_at,
          job_completed_at: apiJob.job_completed_at,
          provider_accepted_at: apiJob.provider_accepted_at,
          job_started_at: apiJob.job_started_at,
          updated_at: apiJob.updated_at
        }));
        
        console.log('📋 Transformed first job:', JSON.stringify(transformedJobs[0], null, 2));
        
        // Sort by most recent first
        transformedJobs.sort((a: Job, b: Job) => {
          const dateA = new Date(a.created_at).getTime();
          const dateB = new Date(b.created_at).getTime();
          return dateB - dateA;
        });
        
        setJobs(transformedJobs);
      } else {
        console.warn('⚠️ Unexpected response format or no data');
        setJobs([]);
      }
    } catch (error) {
      console.error('❌ Error fetching completed jobs:', error);
      toast({
        title: 'Error',
        description: 'Failed to load completed jobs',
        variant: 'destructive',
      });
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  // Get filtered jobs based on date range
  const getFilteredJobs = (): Job[] => {
    if (!fromDate && !toDate) {
      return jobs;
    }

    return jobs.filter(job => {
      const jobDate = new Date(job.created_at);
      let isInRange = true;

      if (fromDate) {
        const fromDateObj = new Date(fromDate);
        fromDateObj.setHours(0, 0, 0, 0);
        isInRange = isInRange && jobDate >= fromDateObj;
      }

      if (toDate) {
        const toDateObj = new Date(toDate);
        toDateObj.setHours(23, 59, 59, 999);
        isInRange = isInRange && jobDate <= toDateObj;
      }

      return isInRange;
    });
  };

  // Generate filename with date range
  const getFilename = (extension: string): string => {
    let filename = 'completed-jobs';
    
    if (fromDate || toDate) {
      if (fromDate && toDate) {
        filename += `-${fromDate}-to-${toDate}`;
      } else if (fromDate) {
        filename += `-from-${fromDate}`;
      } else if (toDate) {
        filename += `-until-${toDate}`;
      }
    } else {
      filename += `-${new Date().toISOString().split('T')[0]}`;
    }
    
    return `${filename}.${extension}`;
  };

  const generateCSV = () => {
    const filteredJobs = getFilteredJobs();
    if (filteredJobs.length === 0) {
      toast({
        title: 'No data',
        description: 'No completed jobs to export',
        variant: 'destructive',
      });
      return;
    }

    const escapeCSV = (value: string | number): string => {
      if (value === null || value === undefined) return '';
      const stringValue = value.toString();
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    };

    const headers = ['Job ID', 'Service', 'Customer', 'Amount ($)', 'Date Completed'];
    const rows = filteredJobs.map(job => [
      escapeCSV(job.id),
      escapeCSV(job.service_name || job.service || ''),
      escapeCSV(job.customer_name || ''),
      escapeCSV(job.amount_gbp || '0'),
      escapeCSV(new Date(job.created_at).toLocaleDateString('en-GB'))
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    downloadFile(csvContent, getFilename('csv'), 'text/csv;charset=utf-8');
  };

  const generateExcel = () => {
    const filteredJobs = getFilteredJobs();
    if (filteredJobs.length === 0) {
      toast({
        title: 'No data',
        description: 'No completed jobs to export',
        variant: 'destructive',
      });
      return;
    }

    // Generate proper Excel XML format (SpreadsheetML)
    let excelContent = '<?xml version="1.0" encoding="UTF-8"?>\n';
    excelContent += '<?mso-application progid="Excel.Sheet"?>\n';
    excelContent += '<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"\n';
    excelContent += ' xmlns:o="urn:schemas-microsoft-com:office:office"\n';
    excelContent += ' xmlns:x="urn:schemas-microsoft-com:office:excel"\n';
    excelContent += ' xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet"\n';
    excelContent += ' xmlns:html="http://www.w3.org/TR/REC-html40">\n';
    excelContent += '<Styles>\n';
    excelContent += '<Style ss:ID="Header">\n';
    excelContent += '<Font ss:Bold="1" ss:Size="11" ss:Color="FFFFFF"/>\n';
    excelContent += '<Interior ss:Color="0070C0" ss:Pattern="Solid"/>\n';
    excelContent += '<Alignment ss:Horizontal="Center"/>\n';
    excelContent += '</Style>\n';
    excelContent += '<Style ss:ID="DataStyle">\n';
    excelContent += '<Alignment ss:Horizontal="Left"/>\n';
    excelContent += '</Style>\n';
    excelContent += '</Styles>\n';
    excelContent += '<Worksheet ss:Name="Completed Jobs">\n';
    excelContent += '<Table>\n';
    excelContent += '<Column ss:AutoFitWidth="0" ss:Width="60"/>\n';
    excelContent += '<Column ss:AutoFitWidth="0" ss:Width="120"/>\n';
    excelContent += '<Column ss:AutoFitWidth="0" ss:Width="120"/>\n';
    excelContent += '<Column ss:AutoFitWidth="0" ss:Width="80"/>\n';
    excelContent += '<Column ss:AutoFitWidth="0" ss:Width="100"/>\n';
    
    // Header row
    excelContent += '<Row ss:StyleID="Header">\n';
    excelContent += '<Cell ss:StyleID="Header"><Data ss:Type="String">Job ID</Data></Cell>\n';
    excelContent += '<Cell ss:StyleID="Header"><Data ss:Type="String">Service</Data></Cell>\n';
    excelContent += '<Cell ss:StyleID="Header"><Data ss:Type="String">Customer</Data></Cell>\n';
    excelContent += '<Cell ss:StyleID="Header"><Data ss:Type="String">Amount ($)</Data></Cell>\n';
    excelContent += '<Cell ss:StyleID="Header"><Data ss:Type="String">Date Completed</Data></Cell>\n';
    excelContent += '</Row>\n';
    
    // Data rows
    filteredJobs.forEach(job => {
      excelContent += '<Row ss:StyleID="DataStyle">\n';
      excelContent += `<Cell><Data ss:Type="Number">${job.id}</Data></Cell>\n`;
      excelContent += `<Cell><Data ss:Type="String">${(job.service_name || job.service || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</Data></Cell>\n`;
      excelContent += `<Cell><Data ss:Type="String">${(job.customer_name || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')}</Data></Cell>\n`;
      excelContent += `<Cell><Data ss:Type="Number">${parseFloat(job.amount_gbp || '0')}</Data></Cell>\n`;
      excelContent += `<Cell><Data ss:Type="String">${new Date(job.created_at).toLocaleDateString('en-GB')}</Data></Cell>\n`;
      excelContent += '</Row>\n';
    });
    
    excelContent += '</Table>\n';
    excelContent += '</Worksheet>\n';
    excelContent += '</Workbook>';

    downloadFile(excelContent, getFilename('xlsx'), 'application/vnd.ms-excel');
  };

  const generatePDF = () => {
    const filteredJobs = getFilteredJobs();
    if (filteredJobs.length === 0) {
      toast({
        title: 'No data',
        description: 'No completed jobs to export',
        variant: 'destructive',
      });
      return;
    }

    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 15;

      // Header background
      doc.setFillColor(0, 112, 192);
      doc.rect(0, 0, pageWidth, 25, 'F');

      // Company name / Logo area
      doc.setFontSize(20);
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, 'bold');
      doc.text('VIREWIRE', 15, 12);

      // Subtitle in header
      doc.setFontSize(9);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(200, 220, 255);
      doc.text('Professional Services Dashboard', 15, 18);

      // Green accent line
      doc.setDrawColor(76, 175, 80);
      doc.setLineWidth(0.5);
      doc.line(0, 25, pageWidth, 25);

      yPosition = 32;

      // Title
      doc.setFontSize(16);
      doc.setTextColor(0, 112, 192);
      doc.setFont(undefined, 'bold');
      doc.text('Completed Jobs Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 8;

      // Subtitle
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.setFont(undefined, 'normal');
      if (fromDate || toDate) {
        doc.text(`Period: ${fromDate || 'Start'} to ${toDate || 'End'}`, pageWidth / 2, yPosition, { align: 'center' });
      } else {
        doc.text('Provider Performance Summary', pageWidth / 2, yPosition, { align: 'center' });
      }
      yPosition += 12;

      // Summary Section
      doc.setFontSize(10);
      doc.setTextColor(50);
      const totalEarnings = filteredJobs.reduce((sum, job) => sum + parseFloat(job.amount_gbp || '0'), 0);
      const avgPerJob = totalEarnings / filteredJobs.length;

      // Summary box background
      doc.setFillColor(240, 248, 255);
      doc.rect(15, yPosition - 7, pageWidth - 30, 18, 'F');

      doc.text(`Total Jobs Completed: ${filteredJobs.length}`, 20, yPosition);
      doc.text(`Total Earnings: $${totalEarnings.toFixed(2)}`, 20, yPosition + 6);
      doc.text(`Average Per Job: $${avgPerJob.toFixed(2)}`, 20, yPosition + 12);
      yPosition += 24;

      // Table headers
      const col1 = 20;
      const col2 = 50;
      const col3 = 100;
      const col4 = 150;
      const col5 = 180;
      const rowHeight = 8;
      const headerY = yPosition;

      // Header background
      doc.setFillColor(0, 112, 192);
      doc.rect(col1 - 2, headerY - 6, pageWidth - 22, rowHeight, 'F');

      // Header text
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text('Job ID', col1, headerY);
      doc.text('Service', col2, headerY);
      doc.text('Customer', col3, headerY);
      doc.text('Amount', col4, headerY);
      doc.text('Date', col5, headerY);

      yPosition = headerY + rowHeight + 2;

      // Data rows
      doc.setTextColor(50, 50, 50);
      doc.setFont(undefined, 'normal');
      doc.setFontSize(9);

      filteredJobs.forEach((job, index) => {
        // Alternate row background
        if (index % 2 === 0) {
          doc.setFillColor(245, 248, 252);
          doc.rect(col1 - 2, yPosition - 6, pageWidth - 22, rowHeight, 'F');
        }

        // Row data
        doc.text(`#${job.id}`, col1, yPosition);
        doc.text(job.service_name || job.service || '', col2, yPosition);
        doc.text(job.customer_name || '', col3, yPosition);
        doc.text(`$${job.amount_gbp}`, col4, yPosition);
        doc.text(new Date(job.created_at).toLocaleDateString('en-GB'), col5, yPosition);

        yPosition += rowHeight;

        // Check if we need a new page
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          // Add header to new pages
          doc.setFillColor(0, 112, 192);
          doc.rect(0, 0, pageWidth, 15, 'F');
          doc.setFontSize(12);
          doc.setTextColor(255, 255, 255);
          doc.setFont(undefined, 'bold');
          doc.text('VIREWIRE', 15, 8);
          doc.setDrawColor(76, 175, 80);
          doc.setLineWidth(0.5);
          doc.line(0, 15, pageWidth, 15);
          yPosition = 20;
        }
      });

      // Footer section
      yPosition += 10;
      
      // Footer divider
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(15, yPosition, pageWidth - 15, yPosition);
      
      yPosition += 5;
      doc.setFontSize(9);
      doc.setTextColor(100);
      doc.setFont(undefined, 'normal');
      doc.text(`Generated on ${new Date().toLocaleString('en-GB')}`, pageWidth / 2, yPosition, { align: 'center' });
      doc.text('This is an automated report from Virewire Professional Services', pageWidth / 2, yPosition + 5, { align: 'center' });
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text('© 2026 Virewire. All rights reserved.', pageWidth / 2, yPosition + 10, { align: 'center' });

      // Download
      doc.save(getFilename('pdf'));

      toast({
        title: 'Success',
        description: 'PDF report downloaded successfully',
      });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate PDF report',
        variant: 'destructive',
      });
    }
  };

  const downloadFile = (content: string, filename: string, mimeType: string) => {
    try {
      const blob = new Blob([content], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast({
        title: 'Error',
        description: 'Failed to download file',
        variant: 'destructive',
      });
    }
  };

  const handleGenerateReport = async () => {
    setExporting(true);
    try {
      if (exportFormat === 'csv') {
        generateCSV();
      } else if (exportFormat === 'excel') {
        generateExcel();
      } else if (exportFormat === 'pdf') {
        generatePDF();
      }
      toast({
        title: 'Success',
        description: `Report generated as ${exportFormat.toUpperCase()}`,
      });
    } catch (error) {
      console.error('Error generating report:', error);
      toast({
        title: 'Error',
        description: 'Failed to generate report',
        variant: 'destructive',
      });
    } finally {
      setExporting(false);
    }
  };

  if (authLoading) {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Authenticating...</p>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  if (!user || user.role !== 'provider') {
    return (
      <ProviderLayout>
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">Not authorized to access this page</p>
            <Button onClick={() => navigate('/provider/login')}>Go to Login</Button>
          </div>
        </div>
      </ProviderLayout>
    );
  }

  return (
    <ProviderLayout>
      <Helmet>
        <title>Completed Jobs - Virewire</title>
        <meta name="description" content="View your completed jobs and historical performance data" />
      </Helmet>

      <div className="space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-gray-900">Completed Jobs</h1>
            <p className="text-gray-600">View your completed jobs history.</p>
          </div>
        </div>

        {/* Jobs Count Summary */}
        <div className="text-sm text-gray-600">
          Total completed jobs: {jobs.length}
        </div>

        {/* Jobs List */}
        <div className="space-y-4">
          {loading ? (
            <Card>
              <CardContent className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              </CardContent>
            </Card>
          ) : jobs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <div className="space-y-3">
                  <p className="text-gray-600 font-semibold">No completed jobs yet</p>
                  <p className="text-sm text-gray-500">Once you complete jobs, they will appear here.</p>
                </div>
              </CardContent>
            </Card>
          ) : (
            jobs.map(job => (
              <Card key={job.id} className="border border-gray-200 hover:shadow-sm transition-shadow cursor-pointer">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 space-y-2">
                      <h3 className="font-bold text-gray-900">
                        {job.service_name || job.service || 'Unknown Service'}
                      </h3>
                      <p className="text-sm text-gray-600">
                        Customer: {job.customer_name || 'Unknown'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(job.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="text-lg font-bold text-green-600">
                      ${job.amount_gbp}
                    </div>
                    <button className="ml-4 text-gray-400 hover:text-gray-600">
                      <MoreVertical className="w-5 h-5" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Date Filter Section */}
        {jobs.length > 0 && (
          <Card className="bg-gray-50 border-gray-200">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Filter by Date Range</h3>
                  <p className="text-sm text-gray-600">
                    Select a date range to filter jobs and reports.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">From Date</label>
                    <input
                      type="date"
                      value={fromDate}
                      onChange={(e) => setFromDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-gray-700">To Date</label>
                    <input
                      type="date"
                      value={toDate}
                      onChange={(e) => setToDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                {(fromDate || toDate) && (
                  <div className="flex items-center justify-between bg-blue-100 border border-blue-300 rounded-md p-3">
                    <p className="text-sm text-blue-700">
                      Showing jobs from {fromDate || 'Start'} to {toDate || 'Today'}
                    </p>
                    <button
                      onClick={() => {
                        setFromDate('');
                        setToDate('');
                      }}
                      className="text-sm text-blue-600 hover:text-blue-800 font-semibold"
                    >
                      Clear Filter
                    </button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reports Section */}
        {jobs.length > 0 && (
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-bold text-gray-900 mb-2">Generate Reports</h3>
                  <p className="text-sm text-gray-600">
                    Export your completed jobs data for record keeping and analysis.
                  </p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700">Total Jobs Completed</p>
                    <p className="text-2xl font-bold text-blue-600">{getFilteredJobs().length}</p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700">Total Earnings</p>
                    <p className="text-2xl font-bold text-green-600">
                      ${getFilteredJobs().reduce((sum, job) => {
                        const amount = parseFloat(job.amount_gbp || '0');
                        return sum + (isNaN(amount) ? 0 : amount);
                      }, 0).toFixed(2)}
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-semibold text-gray-700">Export Format</p>
                    <select 
                      value={exportFormat}
                      onChange={(e) => setExportFormat(e.target.value as 'pdf' | 'csv' | 'excel')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white text-gray-700"
                    >
                      <option value="csv">CSV</option>
                      <option value="excel">Excel</option>
                      <option value="pdf">PDF</option>
                    </select>
                  </div>
                </div>

                <Button 
                  onClick={handleGenerateReport}
                  disabled={exporting}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  {exporting ? 'Generating...' : 'Generate Report'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </ProviderLayout>
  );
};

export default ProviderCompletedJobsPage;
